import { useCallback, useState } from "react";
import type { ToastType } from "@legal-500/legal500-design-system";
import { submissionsService, type Submission } from "@/api/submissions.service";
import { useMutation } from "@/hooks/useApi";

type UseSubmissionDeleteOptions = {
  /** Called after a successful delete — e.g. the list `refetch`. */
  onDeleted: () => void;
  /** Surfaces success/error feedback via the page's Toast. */
  notify: (message: string, type: ToastType) => void;
};

/**
 * Owns the force-delete flow for the Submissions list (SOFTSUPP-312 Case 2):
 * selected-row state, the delete mutation, and success/error toasts. Kept out of
 * the page component to keep it focused and under the file-length budget.
 */
export function useSubmissionDelete({ onDeleted, notify }: UseSubmissionDeleteOptions) {
  const [target, setTarget] = useState<Submission | null>(null);
  const { mutate, loading: isDeleting } = useMutation<void, [string]>(id =>
    submissionsService.forceDelete(id),
  );

  const openDelete = useCallback((submission: Submission) => {
    setTarget(submission);
  }, []);

  const closeDelete = useCallback(() => {
    if (!isDeleting) {
      setTarget(null);
    }
  }, [isDeleting]);

  const confirmDelete = useCallback(async () => {
    if (!target?.submissionId) return;
    try {
      await mutate(target.submissionId);
      notify("Submission deleted", "success");
      setTarget(null);
      onDeleted();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete submission";
      notify(message, "error");
    }
  }, [target, mutate, notify, onDeleted]);

  return {
    target,
    isDeleteModalOpen: target !== null,
    isDeleting,
    openDelete,
    closeDelete,
    confirmDelete,
  };
}
